import { EUserGroup, EUserStatus, EChannelType, 
         ETeamType, EContactStatus, ERequestStatus,
         ISettings, IDataCount, IBase, IPerson, IUser, IUserToken, IOTP, 
         ITeam, IChannel, IContact, IChannels, 
         IServiceProvider, IServiceCatalog, IPosition, 
         IAddress, ICountry, ISearchParams, 
         IOrganization
} from "./shared/types";

import { Request } from "./Request";
import { Paragraph } from "./Paragraph";
import { Comment } from "./Comment";
import { Job } from "./Job";

import { ServiceCatalog } from "./ServiceCatalog";
import { Channel } from "./Channel";

import { Base } from "./Base";

import { isNumeric, isString, isObject, isArray, 
         sortObjectArray,
         isEmptyObject
} from "@/common/utils";

import i18n from "@/common/filters";
import * as http from "@/common/http";
import axios from "axios";

//const WS = require("nativescript-websockets");

/**
 * properties mapping between IUser and IUserProperties for chat server
 * Settings will be stored using user preference
 */
const USER_PROPS_MAPPING = {
  HalloId: 'id',
  UserHalloId: 'props.hallo_id',
  Code: 'username',
  Name: 'nickname',
  Catchword: 'props.caption',
  Avatar: null, //data/users/images/+id+/profile.png
  CoverImage: null, //data/users/images/+id+/cover.png
  Region: {
    Name: 'props.country_name',
    Code: 'props.country_code',
    IDC: 'props.country_idc'
  },
  Email: 'email',
  Mobile: 'props.mobile',
  QRCode: 'props.qrcode',
  Prefix: 'props.prefix',
  GivenName: 'first_name',
  Surname: 'last_name',
  Gender: 'props.gender',
  Birthday: 'props.birthday',
  NationalId: 'props.ssn',
  PassportNo: 'props.passport_no',
  Address: {
    AddressNo: 'props.addr_no',
    Moo: 'props.addr_moo',
    Alley: 'props.addr_alley',
    Street: 'props.addr_street',
    Village: 'props.addr_village',
    Town: 'props.addr_town',
    County: 'props.addr_county',
    Province: 'props.addr_province',
    Country: 'props.addr_country',
    ZipCode: 'props.addr_zipcode'
  },
  Settings: {
    Language: 'locale'
  },
  
  Group: 'props.ugroup',
  Status: null, //calculated using delete_at and email_verified properties
  Position: {
    Code: 'position'
  },
  TeamId: 'position',
  Superordinate: 'props.superordinate', //id only, need to further get details
  Subordinates: 'props.subordinate', //comma list of ids
  //Password: 'password', //hash using bcrypt(password,10), in PG: crypt(password,gen_salt(bf,10))
  Facebook: null,
  Twitter: null,
  Google: null,
  //ReservedAuthService: 'auth_service',
  //ReservedRoles: 'roles',
  
  OrganizationCode: 'props.org_code',
  ResponsibleAreaCode: 'props.aor_code'
}
const TEAM_PROPS_MAPPING = {
  HalloId: 'id',
  Code: 'name',
  Name: 'display_name',
  Catchword: 'description',
  Avatar: null,
  CoverImage: null,
  Region: null,
  Email: 'email', //email of the team owner
  Mobile: null,
  QRCode: null,

  Type: 'type', //'O'=>Oficial,'I'=>Group
  Upline: 'allowed_domains', //IContact.HalloId => allowed_domains
  Members: null, //IContact[]  getTeamMembersByTeamId
  //a Channel contains one or more Contact
  Channels: null //IChannel[]  getChannelsByTeamId
  
  //allowed_domains: string;
  //invite_id: string;
  //allow_open_invite: boolean;
}
const CHANNEL_PROPS_MAPPING = {
  HalloId: 'id',
  Code: 'name',
  Name: 'display_name',
  Catchword: 'purpose',
  Avatar: 'header', //adapt this string for image url
  CoverImage: null,
  Region: null,
  Email: 'email',
  Mobile: null,
  QRCode: null,
  Type: 'type', //string to enumber
  Team: 'team_id', //use team_id to get Team
  Unread: 'total_msg_count', //need to check if this is unread
}
const ChineseJapanessKorean = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/;

export class User extends Base implements IUser {
  HalloId: string;
  UserHalloId: string;
  Code?: string;
  Name: string;
  Catchword: string;  //a message describing personality
  Avatar: string  = '/static/images/avatar.png';
  CoverImage:string = '/static/images/user.cover.jpg';
  Region: ICountry = {
    Name: 'Thailand',
    Code: 'TH',
    IDC: '+66'
  };
  Email: string;
  Mobile: string;
  QRCode: string;
  Prefix: string;
  GivenName: string;
  Surname: string;
  Gender: 'male' | 'female';
  Birthday: Date;
  NationalId: string;
  PassportNo: string;
  Address: IAddress = {};
  Group: EUserGroup = EUserGroup.Citizens; //IMPORTANT to set default
  Status: EUserStatus = null;
  Password: string;
  Facebook: string;
  Twitter: string;
  Google: string;
  //employment
  TeamCode: string; //used for selecting requests that belong to the team
  Superordinate: IUser;
  Subordinates: Array<IUser>;
  Position: IPosition;
  Organization: IOrganization;

  LoginStatus: number = 0;
  Token: IUserToken;
  
  RegisterDate?:Date;
  Settings: ISettings = {
    NotifyMessage: true,
    NotifyVideoCall: true,
    ShowPreviewText: true,
    EnableAlertSound: true,
    EnableVibrate: true,
    EnableNightMode: true,
    //IPrivacy;
    InvitationRequireConfirmation: true,
    AllowFindMeByPhone: true,
    AllowFindMeByHalloId: true,
    AllowFindMeByEmail: true,
    AllowAddMeByGroupChat: true,
    AllowAddMeByQRCode: true,
    AllowAddMeByContactCard: true,
    AllowFindMyRequestMonth: null,
    BlockedLists:[],
    //IGeneral
    Language: 'th',
    TextSize: 14,
    Background: '',
    AutoSavePhoto: true,
    AutoSaveVideo: true,
    TurnOffSpeaker: true,
    Stickers: []
  };

  ResponsibleAreas:Array<{[key:string]:string}> = [];
  ResponsibleAreaCode: string = null;
  ServiceCatalog: ServiceCatalog[];
  Requests: Request[];

  ListIndex?:number = 0; //for UI control
  Selected?:boolean = false; //for UI control

  protected _basePropertyNames = [
    'HalloId', 'Code','Name', 'Catchword', 'Avatar', 'CoverImage', 'Region', 'Email', 'Mobile', 'QRCode'
  ];
  protected _personProperNames = [
    ...this._basePropertyNames,
    'Prefix', 'GivenName', 'Surname', 'Gender'
  ];
  protected _personPrivacyPropertyNames = [
    ...this._personProperNames,
    'NationalId', 'PassportNo', 'Birthday', 'Address'
  ]
  protected _employeePropertyNames = [
    ...this._personPrivacyPropertyNames,
    'Organization', 'Position', 'ResponsibleAreaCode'
  ]

  protected _propertyNames = [
    ...this._employeePropertyNames,
    'Group', 'Status', 'Code', 'Password', 'RegisterDate', 
    'Facebook', 'Twitter', 'Google', 'Settings', 'LoginStatus', 'Token'
  ];

  private _NameSuffix: string = '';
  private _hasLoggedIn: boolean = false;
  private _hasRequestPermissionForCamera: boolean = false;

  private _channels: IChannels = {
    All: [],
    Invited: [],
    Official: [],
    Group: [],
    Individual: []
  };

  constructor(user?:IUser) {
    super();
    if (user) {
      this.Properties = user;
    }
  }

  set Properties(user:IUser) {
    for (let p in user) {
      if (p=='Settings') {
        for (let s in user[p]) {
          if (this.Settings[s]!==undefined) {
            /*if (s=='BlockedLists') {
              this.Settings[s] = user[p][s].map((c:IContact) => {
                return new Contact(c);
              },this);
            } else {*/
              this.Settings[s] = user[p][s];
            //}
          }
        };
      } else if (p=='Birthday') {
        if (user[p] instanceof Date) {
          this[p] = user[p];
        } else if (user[p]) {
          this[p] = new Date(user[p]);
        }
      } else if (p=='Superordinate') {
        this[p] = <IUser> user[p];
      } else if (p=='Subordinates') {
        this[p] = user[p].map((user) => {
          return <IUser> user;
        });
      } else if (this._propertyNames.indexOf(p) != -1) {
        this[p] = user[p];
      }
    }
    if (!this.Settings.Language) {
      this.Settings.Language = this.DeviceLanguage;
    }
  }
  
  set PropertiesFromStorage(user:any) {
    let lang = this.DeviceLanguage;
    user.id = user.id || user.uid;
    this.HalloId = user.id;
    user.username = user.username || user.uname;
    this.Code = user.username;
    user.given_name = user.given_name || user['name_' + lang] || user.name;
    this.GivenName = user.given_name;
    user.surname = user.surname || user['surname_' + lang];
    this.Surname = user.surname;
    user.nickname = user.nickname || user['nickname_' + lang];
    this.Name = user.nickname || '';
    user.catchword = user.catchword || user.caption;
    this.Catchword = user.catchword || '';
    this.Region = user.region || {
      Name: 'Thailand',
      Code: 'TH',
      IDC: '+66'
    };
    let ssn = user.ssn || user.ssn_pk || user.ssn_fk;
    if (ssn) {
      ssn = ('000000000000'+ssn).slice(-13);
      if (this.validateThaiId(ssn)) {
        this.NationalId = ssn + '';
      }
    }
    if (user.mobile) {
      if (user.mobile.indexOf('+')==0) {
        let m = user.mobile.split('-');
        this.Region.IDC = m[0];
        try {
          m.splice(0,1);
        } catch (e) {}
        user.mobile = m.join('');
      }
      this.Mobile = user.mobile;
    } else {
      this.Mobile = null;
    }
    this.Email = user.email;

    user.ugroup = user.ugroup || user.group;
    this.Group = user.ugroup;

    user.ustatus = user.ustatus || user.status;
    this.Status = user.status;

    this.TeamCode = user.team;

    if (user.position) {
      this.Position = {
        Code: user.position.code,
        Name: user.position['name_' + this.DeviceLanguage]
      }
    }

    if (user.orgc) {
      this.Organization = {
        Code: user.orgc,
        Abbr: user['org_abbr_' + this.Language],
        Name: user['org_name_' + this.Language]
      };
    }

    user.area_code = user.area_code || user.aor_code;
    this.ResponsibleAreaCode = user.area_code ? (user.area_code + '') : null;
    
    this.Selected = (user.is_lead!==undefined);
  }

  get Properties() : IUser {
    return <IUser>this.getProperties();
  }

  /**
   * all number type in props must be casted to string
   */
  /*
  get PropertiesForChat() : IUserProperties {
    let user:IUserProperties = {
      username: this.Code || ''
    }
    this.ResponsibleAreaCode = this.ResponsibleAreaCode || null;
    let k, v, kv,k2,v2, kv2;
    for (k in USER_PROPS_MAPPING) {
      v = USER_PROPS_MAPPING[k];
      if (v && this[k]!==undefined && this[k]!==null) {
        if (isString(v)) {
          kv = v.split('.');
          if (kv.length == 1) {
            user[v] = this[k];
          } else if (kv.length==2) {
            user[kv[0]] = user[kv[0]] || {};
            if (k=='Superordinate') {
              if (this[k] && this[k].HalloId) {
                user[kv[0]][kv[1]] = this[k].HalloId;
              }
            } else if (k=='Subordinates') {
              if (this[k]) {
                let ids:string[] = [];
                this[k].forEach((s) => {
                  ids.push(s.HalloId);
                });
                user[kv[0]][kv[1]] = ids.join(',');
              }
            } else if (k=='Birthday') {
              user[kv[0]][kv[1]] = this[k].getTime() + '';
            } else if (k=='Group') {
              user[kv[0]][kv[1]] = this[k] + '';
            } else {
              user[kv[0]][kv[1]] = this[k] + '';
            }
          }
        } else if (isObject(v)) {
          for (k2 in v) {
            if (v[k2] && this[k][k2]) {
              kv2 = v[k2].split('.');
              if (kv2.length == 1) {
                user[kv2[0]] = this[k][k2];
              } else if (kv2.length==2) {
                user[kv2[0]] = user[kv2[0]] || {};
                user[kv2[0]][kv2[1]] = this[k][k2] + '';
              }
            }
          }
        }
      }
    }
    
    user.roles = user.roles || 'system_user';
    if (this.Group==EUserGroup.Administrators && !user.roles.match(/system_admin/i)) {
      user.roles = 'system_admin ' + user.roles;
    }
    return user;
  }*/

  get Base() : IBase {
    return this.BaseProperties;
  }

  get BaseProperties() : IBase {
    return <IBase> this.getProperties(this._basePropertyNames);
  }

  get PersonProperties() : IPerson {
    return <IPerson> this.getProperties(this._personProperNames);
  }

  get ContactProperties() : IContact {
    return <IContact> Object.assign(this.getProperties(this._personProperNames), {
      Type: ETeamType.Individual,
      Status: EContactStatus.Invited,
      Owner: this.PersonProperties,
      Members: []
    });
  }

  /*get StateProperties() : IUserState {
    return {
      HalloId: this.HalloId,
      Avatar: this.Avatar,
      //GivenName: this.GivenName,
      //Surname: this.Surname,
      FullName: this.FullName,
      Nickname: this.Name,
      Organization: this.Organization,
      Group: this.Group,
      Token: this.Token,
      Language: this.Language
    }
  }*/

  get hasLoggedIn() : boolean {
    return this._hasLoggedIn;
  }

  set hasLoggedIn(value:boolean) {
    this._hasLoggedIn = value;
  }

  get AvatarURL() : string {
    return this.Avatar;
  }

  get isNew() : boolean {
    return !this.HalloId;
  }

  get isSuperAdmins() : boolean {
    //admin of the system owner, i.e. GISTDA
    return (this.hasLoggedIn && (this.Group == EUserGroup.Administrators));
  }

  get isAdmins() : boolean {
    //admins must have organization registered
    return (this.hasLoggedIn && !!this.OrgCode && (this.Group == EUserGroup.ApplicationSupports));
  }

  //depreciated ...
  get isAppSupports() : boolean {
    //app supports must have organization registered
    return (this.hasLoggedIn && !!this.OrgCode && (this.Group == EUserGroup.ApplicationSupports));
  }

  get isSectionChiefs() : boolean {
    return (this.hasLoggedIn && (this.Group == EUserGroup.SectionChiefs));
  }

  get isTeamLeads() : boolean {
    return (this.hasLoggedIn && (this.Group == EUserGroup.TeamLeads));
  }

  get isImplementers() : boolean {
    return (this.hasLoggedIn && (this.Group == EUserGroup.TeamMembers));
  }

  get isOperators() : boolean {
    return (this.hasLoggedIn && (
           (this.Group == EUserGroup.Operators) ||
           (this.Group == EUserGroup.OperatorsOthers)));
  }

  get isManagers() : boolean {
    return (this.hasLoggedIn && (
            this.Group==EUserGroup.Executives || 
            this.Group==EUserGroup.Governors || 
            this.Group==EUserGroup.Directors || 
            this.Group==EUserGroup.Manager || 
            this.Group==EUserGroup.DivisionManagers || 
            this.Group==EUserGroup.SectionChiefs));
  }
  
  get isCatalogManagers() : boolean {
    return (this.hasLoggedIn && (
      this.Group==EUserGroup.DivisionManagers || 
      this.Group==EUserGroup.SectionChiefs || 
      this.Group==EUserGroup.TeamLeads));
  }

  get isOfficials() : boolean {
    return (this.hasLoggedIn && (this.Group != EUserGroup.Citizens));
  }

  get isCitizens() : boolean {
    return (this.hasLoggedIn && (this.Group == EUserGroup.Citizens));
  }
  
  get OrgCode() : string {
    return (this.hasLoggedIn && this.Organization && !!this.Organization.Code) ?
            this.Organization.Code : null;
  }

  get isDoae() : boolean {
    return (this.hasLoggedIn && this.Organization && !!this.Organization.Code.match(/^doae/i));
  }

  get isGistda() : boolean {
    return (this.hasLoggedIn && this.Organization && !!this.Organization.Code.match(/^gistda$/i));
  }

  get isOtherOrg() : boolean {
    return (this.hasLoggedIn && this.Organization && !this.Organization.Code.match(/^doae/i));
  }

  get isFarmer() : boolean {
    return (this.hasLoggedIn && (this.Group == EUserGroup.Citizens));
  }

  get canEdit() : boolean {
    return this.isSuperAdmins || this.isAppSupports || this.isOperators;
  }

  get ManagerId() : string {
    return this.Superordinate ? this.Superordinate.HalloId : null;
  }

  get AppRootPath() : string {
    let ret: string;
    //if (application.android) {
    //  ret = application.android.context.getPackageCodePath();
    //} else if (application.ios) {
    //  ret = 'todo 4 ios';
    //}
    return ret;
  }

  get AppPath() : string {
    //return fileSystem.knownFolders.currentApp().path;
    return null;
  }

  /**
   * Inidicate if the user's device has camera or not
   */
  get hasCamera() : boolean {
    //return isAvailable()===true;
    return false; // todo: check webcam or userMedia support
  };

  get Id() : string {
    return this.UserHalloId || this.HalloId;
    //this.HalloId = '';//ensure it is a string
    //return (this.HalloId.match(/^hallo/i) && (this.HalloId.length>5)) ? this.HalloId.substr(5) : this.HalloId;
  }

  get Language() : string {
    return this.Settings.Language || this.DeviceLanguage;
  }

  set Language(value:string) {
    this.Settings.Language = value;
  }

  get IDCMobileNumber() : string {
    let icc = this.Region.IDC || '';
    let mbn = this.validateMobile(this.Mobile);
    if (icc && mbn) {
      mbn = (this.Region.Code=='TH') ? mbn.substr(0,3) + ' ' + mbn.substr(3,3) + ' ' + mbn.substr(6) :
            (this.Region.Code=='CN') ? mbn.substr(0,3) + ' ' + mbn.substr(3,4) + ' ' + mbn.substr(7) :
            mbn;
      return icc + ' ' + mbn;
    }
    return null;
  }

  get FullName() : string {
    let name:string = '';
    if (this.GivenName && this.Surname) {
      if (this.GivenName.match(ChineseJapanessKorean) && this.Surname.match(ChineseJapanessKorean)) {
        name = this.Surname + this.GivenName;
      } else {
        name = this.GivenName + ' ' + this.Surname;
      }
    } else {
      name = this.GivenName || this.Surname;
    }
    return name;
  }

  get DisplayName() : string {
    return this.Name || this.FullName;
  }

  get GroupName() : string {
    let s:string = EUserGroup[this.Group];
    s = s.charAt(0).toLowerCase() + s.slice(1);
    return i18n.L(s);
  }

  get OrgAbbr() : string {
    return this.Organization && this.Organization.Abbr ?
           this.Organization.Abbr : '-';
  }

  get OrgName() : string {
    return this.Organization ? this.Organization.Name : null;
  }

  get IdAndGroupText() : string {
      let text = this.Code || this.Id;
      let group:EUserGroup = this.Group;
      if (group!=EUserGroup.Citizens) {
          text += '/' + this.GroupName;
      }
      return text;
  }

  get RegionText() : string {
    let text:string = '';
    let region:ICountry = this.Region;
    if (!region) {
      return null;
    } else {
    text = region.Name;
      if (this.Language=='zh') {
        if (region.Province) {
          text += region.Province.Name;
          if (region.Province.Amphoe) {
            text += region.Province.Amphoe.Name;
            if (region.Province.Amphoe.Tambon) {
              text += region.Province.Amphoe.Tambon.Name;
            }
          }
        }
      } else {
        if (region.Province) {
          text = region.Province.Name + ',' + text;
          if (region.Province.Amphoe) {
            text = region.Province.Amphoe.Name + ',' + text;
            if (region.Province.Amphoe.Tambon) {
              text = region.Province.Amphoe.Tambon.Name + ',' + text;
            }
          }
        }
      }
    }
    return text;
  }

  get ResponsibleAreaText() : string {
    let text;
    if (this.ResponsibleAreaCode && this.ResponsibleAreas.length>0) {
      let name = 'name_' + this.DeviceLanguage;
      for (let i=0; i<this.ResponsibleAreas.length; i++) {
        if (this.ResponsibleAreas[i].code==this.ResponsibleAreaCode) {
          text = this.ResponsibleAreas[i][name];
          break;
        }
      }
    }
    return text;
  }

  get DeliveryAddress() : string {
    let s:string,addr:string[] = [];
    if (this.Address) {
      if (this.Address.Alley) addr.push(this.Address.Alley);
      if (this.Address.Street) addr.push(this.Address.Street);
      if (this.Address.Village) addr.push(this.Address.Village);
      if (this.Address.Moo) addr.push(this.Address.Moo);
      if (this.Address.Town) addr.push(this.Address.Town);
      if (this.Address.County) addr.push(this.Address.County);
      if (this.Address.Province) addr.push(this.Address.Province);
      if (this.Region) addr.push(this.Region.Name);
      if (this.Language=='zh') {
        if (this.Address.ZipCode) {
          addr.reverse();
          if (this.Address.AddressNo) addr.push(this.Address.AddressNo);
          s = addr.join('');
          if (this.Address.ZipCode) {
            s += '，' + this.Address.ZipCode;
          }
        }
      } else {
        if (this.Address.ZipCode) {
          addr.push(this.Address.ZipCode);
        }
        s = (this.Language=='th') ? addr.join(' ') : addr.join(', ');
        if (this.Address.AddressNo) {
          s = this.Address.AddressNo + ' ' + s;
        }
      }
    }
    return s || '-';
  }
  
  get QRCodeDisplayName() : string {
    return this.RegionText || this.Email || this.Catchword || this.Code || this.Id;
  }

  isUpdated(properties:IUser) : boolean {
    let ret:boolean = false;
    let p:string;
    for (p in properties) {
      if (properties[p]!==undefined) {
        if (p=='Birthday') {
          let bd = properties[p];
          if (this.Birthday) {
            return (this.Birthday.getFullYear()!=bd.getFullYear() || 
                    this.Birthday.getMonth()!=bd.getMonth() || 
                    this.Birthday.getDate()!=bd.getDate());
          } else {
            return (this.Birthday!=bd);
          }
        } else {
          if (properties[p] != this[p]) {
            return true;
          }
        }
      }
    }
    return ret;
  }

  private _loadingAvatar: boolean = true;
  get LoadingAvatar() : boolean {
    return this._loadingAvatar;
  }
  
  onAvatarLoaded(args) {
    setTimeout(() => {
      this._loadingAvatar = false;
    },500);
  }

  //called from QRCode.ts onInit, if this does not exist
  /*generateQRCode(size?:number) : string {
    if (size==undefined || size==0) {
      size = 16;
    }
    let zx = new ZXing();
    //todo: overlay Hallo logo at the center of the QR code
    //set text to https://hallo.softoo.co.th/rest/{encoded HalloId+action to add contact}
    //when a friend scan the QR Code, go the above url to add it to his/her contact list
    let img = zx.createBarcode({encode: this.HalloId + '', height: size, width: size, format: ZXing.QR_CODE});
    let imgFile = new ImageSource();
    imgFile.setNativeSource(img);
    return this._saveAppImage(imgFile, 'QRCode_' + this.HalloId, 'png');
    //return 'data:image/png;base64,' + imgFile.toBase64String('png');
  }

  private _scanQRCode() : void {
      let options = {
          continuousScanCallback: (result) => { //IMPORTANT: use ES6 arrow function to pass 'this' to the callback
              barcodeScanner.stop()
              .then(() => {
                  this._addContactQRCode(result.text);
              });
          }
          ,formats: "QR_CODE, EAN_13"
          ,cancelLabel: i18n.L(['my','^QRCode$','ofMine']) // "EXIT. Also, try the volume buttons!", // iOS only, default 'Close'
          ,cancelLabelBackgroundColor: "#333333" // iOS only, default '#000000' (black)
          ,message: i18n.L('sScanQRCode') //"Use the volume buttons for extra light", // Android only, default is 'Place a barcode inside the viewfinder rectangle to scan it.'
          //,showFlipCameraButton: false   // default false
          //,preferFrontCamera: false     // default false
          //,showTorchButton: true        // default false
          //,beepOnScan: true             // Play or Suppress beep on scan (default true)
          //,torchOn: true               // launch with the flashlight on (default false)
          //,resultDisplayDuration: 10   // Android only, default 1500 (ms), set to 0 to disable echoing the scanned text
          ,orientation: 'portrait'     // Android only, default undefined (sensor-driven orientation), other options: portrait|landscape
          ,openSettingsIfPermissionWasPreviouslyDenied: true // On iOS you can send the user to the settings app if access was previously denied
      };
      barcodeScanner.scan(options)
      .then((result) => {
          //this is not called back. instead, use 'continuousScanCallback' function    
      }, (error) => {
          console.log("scanQRCode Error: " + error);
      });
  }

  scanQRCode() : void {
      barcodeScanner.hasCameraPermission()
      .then((granted) => {
          if (granted) {
              this._scanQRCode();
          } else {
              barcodeScanner.requestCameraPermission()
              .then((what) => {
                  this._scanQRCode();
              });
          };
      });
  }

  async getImagePath(image:string | MediaAsset) : Promise<string> {
    let path:string;
    try {
      if (image instanceof MediaAsset) {
        path = await (<MediaAsset>image).getPath();
      } else {
        //return new Promise((resolve,reject) => {
        //  resolve(<string>image);
        //});
        path = <string>image;
      }
      return path;
    } catch (error) {
      throw error;
    }
  }

  async fetchMedia(options?:IMediaQueryOptions) : Promise<ObservableArray<MediaAsset>> {
    try {
      options = options || {};
      // by design, this.MediaAssets is sorted by media taken date desc
      // by when fetch, we get the results sorted asc so that we prepend
      // the result to the beginning of this.MediaAssets
      options.sortOrder = 'asc'; //options.sortOrder || 'asc';
      this.MediaAssets = this.MediaAssets || new ObservableArray<MediaAsset>([]);
      if (this.MediaAssets.length>0) {
        options.takenAfter = this.MediaAssets.getItem(0).dateTaken;
      }
      let mStore = new MediaStore(options.assetOptions);
      await mStore.authorize();
      let photos = await mStore.fetch(options);
      photos.forEach((p) => {
        p.set('icons',this.icons);
        p.set('D',this.D);
        this.MediaAssets.unshift(p);
      });
      return this.MediaAssets;
    } catch (error) {
      let L = i18n.L;
      dialogs.alert({
        title: L('error'),
        message: error.message,
        okButtonText: L('close')
      });
    }
  }

  takePhoto() : Promise <MediaAsset> {
    let self = this;
    return new Promise((resolve,reject) => {
      try {
        requestPermissions();
        takePicture({
          keepAspectRatio: true, 
          saveToGallery: true
        })
        .then((imageAsset) => {
          if (!self.MediaAssets) {
            //this will include the newly taken photo
            self.fetchMedia().then((ms) => {
              resolve(self.MediaAssets.getItem(self.MediaAssets.length-1));
            });
          } else {
            //add newly taken photo to the store
            let ma:MediaAsset;
            if (imageAsset.ios) {
              ma = new MediaAsset(imageAsset.ios, self.MediaAssets.getItem(0).options);
              ma.set('icons',this.icons);
              ma.set('D', this.D);
              self.MediaAssets.unshift(ma);
              resolve(ma);
            } else {
              //android
              let path = imageAsset.android;
              let mStore:MediaStore = new MediaStore(self.MediaAssets.getItem(0).options);
              mStore.broadCastChange(path);
              setTimeout(()=>{
                mStore.authorize().then(() => {
                  mStore.fetch({pathIs: path}).then((assets)=>{
                    if (assets.length>0) {
                      ma = assets[0];
                      ma.set('icons',this.icons);
                      ma.set('D', this.D);
                      self.MediaAssets.unshift(ma);
                      resolve(ma);
                    } else {
                      //try it one more time
                      mStore.fetch({pathIs: path}).then((assets)=>{
                        if (assets.length>0) {
                          ma = assets[0];
                          ma.set('icons',this.icons);
                          ma.set('D', this.D);
                          self.MediaAssets.unshift(ma);
                          resolve(ma);
                        } else {
                          reject (new Error('Failed to query the new image'));
                        }
                      },(error) => {
                        reject(error);
                      });
                    }
                  },(error)=>{
                    reject(error);
                  });
                });
              },300); //waiting for android to update the media store;
            }
          }
        }, (error) => {
          reject(error); //including cancelled
        });
      } catch (error) {
        reject(error);
      }
    });
  }
  
  share(what?: 'Avatar' | 'QRCode') : void {
    try {
      this._createImageSource(this[what])
      .then((imgFile:ImageSource) => {
        if (imgFile && imgFile.width>0 && imgFile.height>0) {
          socialShare.shareImage(imgFile, i18n.L('messageShare'));
        }
      },(error) => {
        dialogs.alert('user.ts share ' + what + ' failed\\n' + error);
      });
    } catch (e) {
      dialogs.alert('user.ts share ' + what + ' failed\\n' + e.message);
    }
  }
  
  saveImage(what?: 'Avatar' | 'QRCode') : void {
    try {
      this._createImageSource(this[what])
      .then((imgFile:ImageSource) => {
        if (imgFile && imgFile.width>0 && imgFile.height>0) {
          try {
            let saved:boolean = imgFile.saveToFile('Hallo_' + what + '_' + this.Name + '.jpg',"jpeg");
            if (!saved) {
              dialogs.alert('user.ts save ' + what + ' as image failed');
            }
          } catch (e) {
            dialogs.alert(e);
          }
        }
      },(error) => {
        dialogs.alert('user.ts saveImage ' + what + ' failed\\n' + error);
      });
    } catch (e) {
      dialogs.alert('user.ts saveImage ' + what + ' failed\\n' + e.message);
    }
  }*/
  
  get AllChannels() : Array<Channel> {
    if (this._channels.All && this._channels.All.length>0) {
      return this._channels.All.map((c) => {
        return new Channel(c);
      });
    }
    return null;
  }
  get InvitedChannels() : Array<Channel> {
    if (this._channels.Invited && this._channels.Invited.length>0) {
      return this._channels.Invited.map((c) => {
        return new Channel(c);
      });
    }
    return null;
  }
  get IndividualChannels() : Array<Channel> {
    if (this._channels.Individual && this._channels.Individual.length>0) {
      return this._channels.Individual.map((c) => {
        return new Channel(c);
      });
    }
    return null;
  }
  get GroupChannels() : Array<Channel> {
    if (this._channels.Group && this._channels.Group.length>0) {
      return this._channels.Group.map((c) => {
        return new Channel(c);
      });
    }
    return null;
  }
  get OfficialChannels() : Array<IChannel> {
    if (this._channels.Official && this._channels.Official.length>0) {
      return this._channels.Official.map((c) => {
        return new Channel(c);
      });
    }
    return null;
  }
  get BlockedContacts() : Array<IContact> {
    return this.Settings.BlockedLists;
  }

  validatePassword(Password:string) : boolean {
    // at least one number, one lowercase and one uppercase letter
    // at least 6 characters
    let re = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}/;
    return re.test(Password+'');
  }

  validateHalloId(id:string) : boolean {
    id += '';
    if (!id) {
      return false;
    }
    return (id.match(/\d+/g) && (id.substr(0,1)=='1') && (id.length==11)) || (id.length>0);
  }

  validateThaiId(value:string) : boolean {
    let id:string = value + '';
    let ok:boolean = isNumeric(value) && (id.length==13);
    if (ok) {
      let i:number,sum:number=0;
      for(i=0; i<12; i++) {
        sum += parseInt(id.charAt(i))*(13-i);
      }
      ok = ((11 - sum % 11) % 10 == parseInt(id.charAt(12)));
    }
    return ok;
  }

  validateEmail(email:string) : boolean {
    let re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email+'');
  }

  validateMobile(mobile, region?:ICountry) : string {
    region = region || this.Region;
    mobile = (mobile+'').replace(/\s/g, ''); //remove all spaces
    let m = mobile.match(/\d+/g);
    m = (m && m.length>0) ? m.join('') : null
    if (m && region) {
      if (region.Code=='TH') {
        if ((m.length!=10) || (m.substr(0,1)!=='0')) {
          m = null;
        }
      } else if (region.Code=='CN') {
        if ((m.length!=11) || (m.substr(0,1)!=='1')) {
          m = null;
        }
      } else if (m.length<6) {
        m = null;
      }
    }
    return m;
  }
  
  private _addContactQRCode(qrcode:string) {
    //the final step from scan QR Code
    //dialogs.alert('QR Code: ' + qrcode + '\nTO DO: add this contact using this QR Code');
  }

  getBindingAccountInfo(accountType) : any {
    let info: any;
    //TO-DO:
    switch (accountType) {
      case 'Facebook':
        info = 'facebook account id';
        break;
      case 'Twitter':
        info = 'twitter account id';
        break;
      case 'Google':
        info = 'google account id';
        break;
    }
    return info;
  }

  async login(idEmailToken:string, password?:string) {
    try {
      let data:any = password ? {
        userName: idEmailToken,
        password: password
      } : {
        token: idEmailToken
      }
      data = await http.request(http.urls.user.url + '/login', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      //CHAT.login(data.username, password); //continue to create a chat session on server
      this.Password = password;
      this.Token = <IUserToken> data.token; //http.USER_TOKEN;
      this.PropertiesFromStorage = data;
      this._hasLoggedIn = true;
      //this.Avatar = CHAT.getProfilePictureUrl(this.HalloId,(new Date()).getTime());
      this.LoginStatus = 1; //allowing auto login the next time when app starts
      this.save();
      this.fetchDataAfterLogin();
      this.openWebSocket();
      if (!password && data.otp_data) {
        return data.otp_data;
      } else {
        return true;
      }
    } catch (e) {
      throw(e);
    }
  }

  async logout() : Promise<boolean> {
    try {
      this.requestResource('user/logout', 'POST');
      this._hasLoggedIn = false;
      this.Token = null;
      return true;
    } catch (e) {
      throw(e);
    }
  }

  private checkToken(resource:string, method:string) : boolean {
    return true;
    /* let the server authenticat request
    if ((resource.match(/^user\/sendotp|^user\/verifyotp|^user$|^user\/login|^user\/signup/i) && method.match(/POST/i)) || 
        (resource.match(/^user\/forgotpassword$/i) && method.match(/PUT/i)) || 
        (resource.match(/^user\/availability|^mis\/public_teams/i)) || 
        (this.Token && this.Token.access)) {
      return true;
    } else {
      throw new Error('A valid token is required');
    } */
  }

  OTP:IOTP;
  async sendOTP(mobileNo?:string) : Promise<any> {
    try {
      mobileNo = mobileNo ? mobileNo.replace(/\s/g,'') : (this.Region.IDC + this.Mobile);
      let otp = await this.requestResource('user/sendotp','POST', {"mobile":mobileNo});
      this.OTP = {
        otp: otp.otp,
        expire: otp.expire,
        key: otp.key
      }
      return otp;
    } catch (error) {
      throw error;
    }
  }
  
  async verifyOTP(otp:string) : Promise<boolean> {
    try {
      if (!(this.OTP && this.OTP.otp==otp)) {
        return false;
      }
      let result = await this.requestResource('user/verifyotp','POST', {"otp":otp,"key":this.OTP.key});
      if (result) {
        return result.verified;
      } else {
        return false;
      }
    } catch (error) {
      throw error;
    }
  }

  requestEmail(emailAddress?:string) : Promise<boolean> {
    emailAddress = emailAddress || this.Email;
    return new Promise((resolve,reject) => {
      /*fake.fakeAnEmail(emailAddress)
      .then((ok:boolean) => {
        resolve(ok);
      });*/
      resolve(true);
    });
  }

  confirmEmil(linkCode?:string) : Promise<boolean> {
    return new Promise((resolve,reject) => {
      /*fake.confirmEmail(linkCode)
      .then((ok:boolean) => {
        resolve(ok);
      });*/
      resolve(true);
    });
  }

  save() {
    //appSettings.setString('HalloUser',JSON.stringify(this.Properties));
  }
  
  requestPasswordToEmail(email) : Promise<boolean> {
    return new Promise((resolve,reject) => {
      //todo, http email to send reset password link for further action
      resolve(true);
    });
  }
  
  async fetchCountries() : Promise<ICountry[]> {
    try {
      let results = await http.request(http.urls.idc.url);
      let countries:Array<ICountry>;
      countries = results.map((c) => {
        if (c.name && c.dial_code) {
          return { Code:c.code, Name:c.name, IDC:c.dial_code };
        }
      }, this);
      sortObjectArray(countries,'Name');
      return countries;
    } catch (error) {
      throw error;
    }
  }

  getDataCount() : Promise <IDataCount> {
    //TO-DO: fetch count of new contact, request, chat and news from server
    return new Promise((resolve,reject) => {
      let counts:IDataCount = {
        Teams: 3,
        Requests: 5,
        Chats: 102,
        News: 0
      }
      resolve(counts);
    });
  }

  async getUserAvatarUrl(user:User) : Promise<string> {
    try {
      let url;
      //url = await CHAT.getProfilePictureUrl(user.HalloId,null);
      if (url) {
        user.Avatar = url;
        return url;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }

  /*
  mapUser(user:IUserProperties) {
    let k, v, kv,k2,v2, kv2;
    if (user.roles && user.roles.match(/system_admin/i)) {
      user.props = user.props || {};
      user.props.ugroup = EUserGroup.Administrators + '';
    }
    let timeZoneOffset = (new Date()).getTimezoneOffset() * 60000;
    for (k in USER_PROPS_MAPPING) {
      v = USER_PROPS_MAPPING[k];
      if (v) {
        if (isString(v)) {
          kv = v.split('.');
          if (kv.length==1) {
            this[k] = user[v] || null;
          } else if (kv.length==2) {
            if (k=='Superordinate') {
              let a = 0;
              //todo
            } else if (k=='Subordinates') {
              let b = 0;
              //todo
            } else if (k=='Birthday') {
              this[k] = (user[kv[0]] && user[kv[0]][kv[1]]) ? new Date(parseInt(user[kv[0]][kv[1]]) + timeZoneOffset) : null;
            } else if (k=='Group') {
              if (user[kv[0]] && user[kv[0]][kv[1]]!==undefined && user[kv[0]][kv[1]]!==null) {
                let g = EUserGroup[parseInt(user[kv[0]][kv[1]])];
                this[k] = g ? EUserGroup[g] : EUserGroup.Citizens;
              } else {
                this[k] = EUserGroup.Citizens;
              }
            } else {
              this[k] = (user[kv[0]] && user[kv[0]][kv[1]]) ? user[kv[0]][kv[1]] : null;
            }
          }
        } else if (isObject(v)) {
          this[k] = this[k] || {};
          for (k2 in v) {
            if (v[k2]) {
              kv2 = v[k2].split('.');
              if (kv2.length == 1) {
                this[k][k2] = user[kv2[0]] ? user[kv2[0]] : null;
              } else if (kv2.length==2) {
                this[k][k2] = (user[kv2[0]] && user[kv2[0]][kv2[1]]) ? user[kv2[0]][kv2[1]] : null;
              }
            }
          }
        }
      }
    }
    this.Language = this.Language || 'th';
    if (user.delete_at!==undefined && user.delete_at>0) {
      this.Status = EUserStatus.Revoked;
    } else {
      this.Status = user.email_verified ? EUserStatus.Active : EUserStatus.WaitingToConfirm;
    }
  }

  async fetchUsers(keyword:string,options?:{[key:string]:any}) : Promise<User[]> {
    if (!(this.hasLoggedIn && (this.isAdmins || this.isManagers))) {
      throw {
        message: 'Only can admins or managers get users'
      };
    } else {
      let data;
      let i, users:Array<User> = [];
      options = options || {};
      try {
        if (keyword) {
          data = await CHAT.searchUsers(keyword,{});
        } else {
          data = await CHAT.doFetch(
            CHAT.UsersRoute + '?page=' + options.page + '&per_page=' + options.per_page, { 
            method:'GET'
          });
        }
        if (data && data.length>0) {
          for (i=0; i<data.length; i++) {
            let user = new User();
            user.mapUser(data[i]);
            users.push(user);
            this.getUserAvatarUrl(user);
          }
          sortObjectArray(users,(this.DeviceLanguage=='zh') ? 'Surname,GivenName' : 'GivenName,Surname');
          return users;
        }
        return null;
      } catch (error) {
        if (error.message=='Unauthorized') {
          let iusers:IUser[];
          iusers = await http.request(http.urls.chat.url, {
            user: this,
            fake: 'fakeUsers',
            fakeOption: options
          });
          users = iusers.map((u) => {
            let user = new User();
            user.Properties = u;
            return user;
          });
          return users;
        } else {
          throw error;
        }
      }
    }
  }

  private mapTeam(team:ITeamProperties) : ITeam {
    let iTeam: ITeam = {};
    let k, v;
    for (k in TEAM_PROPS_MAPPING) {
      v = TEAM_PROPS_MAPPING[k];
      if (k=='Type' && v) {
        if (team[v]=='O') {
          iTeam.Type = ETeamType.Official;
        } else {
          iTeam.Type = ETeamType.Group;
        }
      } else if (k=='Upline' && v) {
        //todo: adapt allowed_domains to store Upline.HalloId
        //then use it to get IUser and finally cast IUse to IContact

      } else if (v) {
        iTeam[k] = team[v];
      }
    }
    if (!iTeam.Avatar) {
      iTeam.Avatar = '/assets/images/hallo.user.png';
    }
    if (!iTeam.CoverImage) {
      iTeam.CoverImage = '/assets/images/user.cover.jpg';
    }
    return iTeam;
  }

  async fetchTeams(keyword:string,options?:{[key:string]:any}) : Promise<ITeam[]> {
    let self:User = this;
    if (!(this.hasLoggedIn && (this.isAdmins || this.isManagers))) {
      throw {
        message: 'Only can admins or managers get users'
      };
    } else {
      try {
        let data;
        options = options || {};
        if (keyword) {
          data = await CHAT.searchTeams(keyword);
        } else {
          data = await CHAT.getTeams(options.page, options.per_page);
        }
        if (data && data.length>0) {
          let teams:Array<ITeam>;
          teams = data.map((d:ITeamProperties) => {
            let team: ITeam = this.mapTeam(d);
            return team;
          });
          sortObjectArray(teams,'Name');
          return teams;
        }
        return null;
      } catch (error) {
        throw error;
      }
    }
  }
  
  private mapChannel(channel, team:ITeam) : IChannel {
    let iChannel:IChannel = {};
    let k, v;
    for (k in CHANNEL_PROPS_MAPPING) {
      v = CHANNEL_PROPS_MAPPING[k];
      if (k=='Type' && v) {
        if (channel[v]=='O') {
          iChannel.Type = EChannelType.Public;
        } else if (channel[v]=='P') {
          iChannel.Type = EChannelType.Private;
        } else if (channel[v]=='G') { //Group
          iChannel.Type = EChannelType.Direct;
        }
      } else if (k=='Team' && v && team) {
        iChannel.Team = team;
      } else if (v) {
        iChannel[k] = channel[v];
      }
    }
    if (!iChannel.Avatar) {
      iChannel.Avatar = '/assets/images/hallo.user.png';
    }
    if (!iChannel.CoverImage) {
      iChannel.CoverImage = '/assets/images/user.cover.jpg';
    }
    return iChannel;
  }*/

  async fetchChannels(keyword?:string, options?:{[key:string]:any}) : Promise<IChannels> {
    if (this._channels && this._channels.All && this._channels.All.length>0) {
      return this._channels;
    }
    try {
      let data = await this.requestResource('mis/channels/' + this.HalloId);
      if (data && data.length>0) {
        this._channels = {
          All: [],
          Invited: [],
          Official: [],
          Group: [],
          Individual: []
        };
        data.forEach((d) => {
          let c:Channel = new Channel();
          c.PropertiesFromStorage = d;
          this._channels.All.push(c);
          if (c.Type==EChannelType.Public) {
            this._channels.Official.push(c);
          } else if (c.Type==EChannelType.Direct) {
              this._channels.Individual.push(c);
          } else if (c.Type==EChannelType.Group) {
            this._channels.Group.push(c);
          } else if (c.Type==EChannelType.Private) {
            if (c.Contacts) {
              if (c.Contacts.length>2) {
                this._channels.Group.push(c);
              } else {
                this._channels.Individual.push(c);
              }
            }
          }
        });
        return this._channels;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }

  /*
  async getOfficialChannels(keyword:string,options?:{[key:string]:any}) : Promise<IChannel[]> {
    if (!this.hasLoggedIn) {
      throw {
        message: 'Only can logged in users get public channel'
      };
    } else {
      try {
        let i;
        let data = await CHAT.getMyTeams();
        let team:ITeam;
        let channels: IChannel[];
        if (data && data.length>0) {
          for (i=0; i<data.length; i++) {
            if (data[i].type=='O' && data[i].name.match(/^public$/i) && data[i].allow_open_invite) {
              team = this.mapTeam(data[i]);
              break;
            }
          }
          if (team) {
            let data = await CHAT.getChannels(team.HalloId, 0, 100);
            if (data) {
              channels = data.map((c) => {
                return this.mapChannel(c, team);
              });
              return channels;
            }
          }
        }
        return null;
      } catch (error) {
        throw error;
      }
    }
  }

  async fetchServiceProviderById(userId:string) : Promise<IServiceProvider> {
    try {
      userId = 'appadm'; //////todo
      let data = await CHAT.getUserByUsername(userId);
      let user:User;
      if (data) {
        user = new User();
        user.mapUser(data);
        this.getUserAvatarUrl(user);
        return {
          HalloId: user.HalloId,
          Code: user.Code,
          Name: user.Name || user.FullName,
          Avatar: user.Avatar
        }
      }
      return null;
    } catch (error) {
      throw(error);
    }
  }*/

  async fetchSubordinates(manager_id:string) : Promise<User[]> {
    try {
      let url = "mis/subordinate_" + this.DeviceLanguage + "?team=uid='" + manager_id + "' OR manager_id='" + manager_id + "'";
      //let url = 'mis/subordinate_' + this.DeviceLanguage + '/' + manager_id;
      let data = await this.requestResource(url);
      let subordinates = [];
      if (data && data.length>0) {
        subordinates = data.map((s,index) => {
          return new User({
            HalloId: s.id,
            GivenName: s.given_name,
            Surname: s.surname,
            Name: s.nickname,
            Email: s.email,
            Mobile: s.mobile,
            Group: s.ugroup,
            ResponsibleArea: s.aor_code
          });
        });
      }
      return subordinates;
    } catch(error) {
      throw error;
    }
  }

  private async fetchDataAfterLogin() : Promise<any> {
    return new Promise((resolve,reject) => {
      //this.fetchMedia();
      //this.fetchServiceCatalogs();
      //this.fetchResponsibleArea();
      //this.fetchRequests();
      resolve(true);
    });
  }

  async fetchServiceProviders(options?:{[key:string] : any}) : Promise <Array<IServiceProvider>> {
    try {
      let sp:IServiceProvider[];
        sp = await http.request(http.urls.request.url + '/mis/requests_last30days',{
        user: this,
        fake: 'fetchServiceProviders',
        fakeOption: options
      });
      return sp;
    } catch (error) {

    }
  }

  async fetchResponsibleArea() : Promise<IBase[]>{
    try {
      if (!this.ResponsibleAreas || this.ResponsibleAreas.length==0) {
        let data = await this.requestResource('codelist/responsible_area');
        if (data && data.length>0) {
          this.ResponsibleAreas = data.map((d) => {
            return d;
          });
        }
      }
      let name = 'name_' + this.DeviceLanguage;
      return this.ResponsibleAreas.map((ra)=>{
        return {
          Name: ra[name],
          Code: ra.code + ''
        };
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * fetch service catalogs immediately after login. any new or updated catalogs will be
   * pushed from server via WebSocket, which shall update this.ServiceCatalogs
   */
  async fetchServiceCatalog(options?:ISearchParams) : Promise<ServiceCatalog[]> {
    try {
      let url = 'codelist/service_catalog_admin_' + this.Language;
      if (options) {
        if (options) {
          if (options.offset>0 && options.limit>0) {
            url = url + '?offset=' + options.offset + '&limit=' + options.limit;
          } else if (options.offset>0) {
            url = url + '?offset=' + options.offset;
          } else if (options.limit>0) {
            url = url + '?limit=' + options.limit;
          }
        }
      } else if (this.ServiceCatalog && this.ServiceCatalog.length>0) {
        return this.ServiceCatalog;
      }
      let iscs = await this.requestResource(url);
      let scs:ServiceCatalog[] = [];
      if (iscs && iscs.length>0) {
        this.ServiceCatalog =  this.ServiceCatalog || [];
        iscs.forEach((isc) => {
          let sc:ServiceCatalog = new ServiceCatalog();
          sc.PropertiesFromStorage = isc;
          if (sc.Manager) {
            sc.Manager.ResponsibleAreaCode = isc.responsible_area_code;
          }
          scs.push(sc);
          this.ServiceCatalog.push(sc);
        });
      }
      return scs;
    } catch (error) {
      throw error;
    }
  }

  async fetchServiceCategory(code:string) : Promise<ServiceCatalog> {
    try {
      let sc:ServiceCatalog;
      await this.fetchServiceCatalog();
      if (this.ServiceCatalog && this.ServiceCatalog.length>0) {
        for (let i=0; i<this.ServiceCatalog.length; i++) {
          sc = this.ServiceCatalog[i];
          if (sc.Code == code) {
            return sc;
          } else {
            sc = null;
          }
        }
      }
      return sc;
    } catch (error) {
      throw error;
    }
  }

  ServiceCatalogManagerCandidates: User[];
  async fetchServiceCatalogManagerCandidates(options?:ISearchParams) : Promise<User[]> {
    try {
      let url = 'mis/person_in_charge';
      if (options) {
        if (options.offset>0 && options.limit>0) {
          url = url + '?offset=' + options.offset + '&limit=' + options.limit;
        } else if (options.offset>0) {
          url = url + '?offset=' + options.offset;
        } else if (options.limit>0) {
          url = url + '?limit=' + options.limit;
        }
      } else if (this.ServiceCatalogManagerCandidates && this.ServiceCatalogManagerCandidates.length>0) {
        return this.ServiceCatalogManagerCandidates;
      }
      let iusers = await this.requestResource(url);
      let candidates: User[] = [];
      if (iusers && iusers.length>0) {
        this.ServiceCatalogManagerCandidates = this.ServiceCatalogManagerCandidates || [];
        iusers.forEach((u) => {
            let user = new User(u);
            candidates.push(user);
            this.ServiceCatalogManagerCandidates.push(user);
        });
      }
      return candidates;
    } catch (error) {
      throw error;
    }
  }

  /**
   * fetch requests immediately after login. any new or updated requests will be
   * pushed from server via WebSocket, which shall update this.Requests
   */
  _fetchingRequests:boolean = false;
  async fetchRequests(options?:ISearchParams) : Promise<Request[]>{
    try {
      if (this._fetchingRequests) {
        return this.Requests; //reject new comers ??? to do
      }
      this._fetchingRequests = true; 
      let data:any = null;
      if (options) {
        if (this.Requests && this.Requests.length>0) {
          if (options.searchNewer) {
            data = {
              search: "update_ts>'" + this.Requests[0].Dates.Updated.toISOStringWithTimeZone() + "'"
            }
          } else if (options.searchOlder) {
            data = {
              search: "update_ts<'" + this.Requests[this.Requests.length-1].Dates.Sent.toISOStringWithTimeZone() + "'"
            }
          } else if (options.search) {
            //this.Requests.splice(0,this.Requests.length);
            data = {
              search: options.search
            }
          }
        } else if (options.search) {
          data = {
            search: options.search
          }
        }
        if (options.offset || options.limit) {
          data = data || {};
          if (options.offset) {
            data.offset = options.offset;
          }
          if (options.limit) {
            data.limit = options.limit;
          }
        }
      } else if (this.Requests && this.Requests.length>0) {
        return this.Requests;
      }
      if (this.isImplementers) {
        data = await this.requestResource('mis/requests_assignee/' + this.HalloId, 'GET', data);
      } else {
        if (this.isCitizens) {
          //Citizens can only see their own requests
          data = data || {};
          data.oid = this.HalloId;
        } else { // if (this.isTeamLeads) {
          //TeamLead can only see requests that they are in charged of
          data = data || {};
          //data.mid = this.HalloId;
          if (data.search) {
            data.search += " AND team_code like '" + this.TeamCode + "%'";
          } else {
            data.search = "team_code like '" + this.TeamCode + "%'";
          }
        }
        data = await this.requestResource('mis/requests','GET', data);
      }
      
      let requests: Request[] = [];
      if (data && data.length>0) {
        this.Requests = this.Requests || [];
        data.forEach((r) => {
          if (this.getRequestIndex(r.id)<0) { //not exists
            let request = new Request();
            request.PropertiesFromStorage = r;
            requests.push(request);
          }
        });
        if (options && options.searchNewer) {
          sortObjectArray(requests,'Dates.Updated,Status');
          requests.forEach((r) => {
            this.Requests.unshift(r);
          });
        } else {
          sortObjectArray(requests,'Dates.Updated desc,Status');
          requests.forEach((r) => {
            this.Requests.push(r);
          });
        }
      }
      this._fetchingRequests = false;
      return requests; //return only incremental results
    } catch (error) {
      this._fetchingRequests = false;
      throw error;
    }
  }

  async fetchRequestContents(request:Request) : Promise<Paragraph[]> {
    try {
      if (!(request.Contents && request.Contents.length>0)) {
        request.Contents = [];
        let cnts = await this.requestResource('mis/request_details/' + request.HalloId);
        if (cnts && cnts.length>0) {
          cnts.forEach((cnt) => {
            let p:Paragraph = new Paragraph();
            p.PropertiesFromStorage = cnt;
            p.Request = request;
            request.Contents.push(p);
          });
        }
      }
      return request.Contents;
    } catch (error) {
      if (error.id!='no_content') {
        throw error;
      }
    }
  }

  async fetchRequestJobs(request:Request) : Promise<Job[]> {
    try {
      if (!(request.Jobs && request.Jobs.length>0)) {
        request.Jobs = [];
        let data = await this.requestResource('mis/request_jobs/' + request.HalloId);
        if (data) {
          data.forEach((d) => {
            let job:Job = new Job();
            job.Request = request;
            job.PropertiesFromStorage = d;
            //implementers can see only those jobs that are assigned to them
            if (!this.isImplementers || (this.isImplementers && job.getAssigneeIndex(this.HalloId)>=0)) {
              request.Jobs.push(job)
            }
          }, this);
          if (request.Jobs.length>0) {
            sortObjectArray(request.Jobs,'DateBegin,DateEnd,Name');
            request.Jobs.forEach((job,index) => {
              job.ListIndex = index;
            });
          }
        }
      }
      return request.Jobs;
    } catch (error) {
      if (error.id!='no_content') {
        throw error;
      }
    }
  }

  async fetchRequestComments(request:Request) : Promise<Comment[]> {
    try {
      if (!(request.Comments && request.Comments.length>0)) {
        request.Comments = [];
        let cmts = await this.requestResource('mis/request_comments/' + request.HalloId);
        if (cmts && cmts.length>0) {
          cmts.forEach((cmt) => {
            let c:Comment = new Comment();
            c.PropertiesFromStorage = cmt;
            c.Request = request;
            request.Comments.push(c);
          }, this);
        }
      }
      return request.Comments;
    } catch (error) {
      if (error.id!='no_content') {
        throw error;
      }
    }
  }

  /**
   * data : {file:File, details:{}}
   */
  uploadFileUsingFetch(resource:string, data:any) {
    try {
      let method = 'POST'; //only POST support multiparts, PUT will fail
      if (this.checkToken(resource,method)) {
        /*let name = data.filePath.substr(data.filePath.lastIndexOf("/") + 1);
        let mimeType = name.match(/\.png$/i) ? 'image/png' :
                       name.match(/\.gif$/i) ? 'image/gif' :
                       name.match(/\.jpg$|jpeg$/i) ? 'image/jpeg' :
                       name.match(/\.tiff$/i) ? 'image/tiff' :
                       name.match(/\.pdf$/i) ? 'application/pdf' :
                       name.match(/\.zip$/i) ? 'application/zip' :
                       name.match(/\.rar$/i) ? 'application/x-rar-compressed' :
                       'application/octet-stream';
        let description = 'Upload file ' + name;
        let request = {
          url: http.urls.request.url + '/' + resource,
          method: method,
          headers: {
            //"File-Name": name,
            "Content-Type": 'multipart/form-data'
            //"Accept": "application/json",
            //"Authorization": "BEARER " + this.Token.access.token,
            //"X-Requested-With": http.APIID
          }
        };*/
        
        let formData = new FormData();
        formData.append(data.fieldName, data.file);
        formData.append('details', JSON.stringify(data.details));
        let result = http.request(http.urls.request.url + '/' + resource, {
          method: method,
          // must not define Content-Type when upload multi-part form data
          // 'set to DELETE_ME' will delete default Content-Type in http.ts
          headers: {
            "Content-Type": 'DELETE_ME' 
          },
          body: formData
        });
        return result;
      }
    } catch (error) {
      throw error;
    }
  }
  
  /**
   * upload file using axios
   * 
   * @param resource 
   * @param data {fieldName:string,file:File,details:{}}
   * @param events 
   */
  async uploadFile(resource:string, data:any, events?:any) {
    let self = this;
    try {
      let method = 'POST'; //only POST support multiparts, PUT will fail
      if (this.checkToken(resource,method)) {
        let options:any = {
          timeout: 4*60*1000, // 0: no timeout
          headers: {
            //"File-Name": file.name, // not allowed in by Access-Control-Allow-Headers in preflight response
            //"Content-Type": "application/octet-stream",
            "Accept": "application/json",
            "X-Requested-With": http.APIID,
            //"Authorization": 'Bearer ' + this.Token.access.token
          },
          cancelToken: new axios.CancelToken(function executor(cancel) {
            // An executor function receives a cancel function as a parameter
            self.cancelAxiosUpload = cancel;
          })
        };
        if (this.Token && this.Token.access && this.Token.access.token) {
          options.headers.Authorization = 'Bearer ' + this.Token.access.token;
        }
        if (events && events.onUploadProgress) {
          options.onUploadProgress = events.onUploadProgress;
        }
        let file = data.file;
        let formData = new FormData();
        formData.append(data.fieldName, file);
        formData.append('details', JSON.stringify(data.details));
        let res = await axios.post(http.urls.request.url + '/' + resource, formData, options);
        return res.data;
      } else {
        throw ({
          id: 'permission_denied',
          message: 'User token is required. Please login to get one'
        });
      }
    } catch (error) {
      self.cancelUpload();
      if (error.response) {
        error = {
          id: 'requestMadeButServerFailed',
          status: error.response.status,
          message: 'The request was made and the server responded with error'
        };
      } else if (error.request) {
        error = {
          id: 'no_server_response',
          message: 'The request was made but no response was received'
        };
      }
      throw error;
    }
  }

  cancelAxiosUpload = null;
  cancelUpload() {
    if (this.cancelAxiosUpload) {
      this.cancelAxiosUpload();
    }
  };

  updateToken(token:IUserToken) {
    this.Token = token;
    http.updateToken(token);
  }

  async requestResource(resource:string, method?:string, data?:any) {
    try {
      method = method || 'GET';
      let requireNoTokens = [
        'sendotp'
      ]
      if (this.checkToken(resource, method)) {
        let url = resource;
        if (url.indexOf('://')==-1) {
          url = http.urls.request.url + '/' + resource;
        }
        let options:any = {
          method: method
        }
        if (data) {
          if (data.headers) {
            options.headers = data.headers;
            delete data.headers;
          }
          if (!isEmptyObject(data)) {
            if (method.match(/GET/i)) {
              url += '?' + Object.keys(data).map(function(k) {
                return encodeURIComponent(k) + '=' + encodeURIComponent(data[k])
              }).join('&');
            } else {
              options.body = JSON.stringify(data);
            }
          }
        } else {
          url = encodeURI(url);
        }
        data = await http.request(url, options);
        return data;
      }
    } catch (error) {
      throw error;
    }
  }

  private updateResource(resource) {
    try {
      switch (resource.type) {
      case 'catalog':
        this.updateServiceCatalogs(resource.action, resource.data);
        break;
      case 'request':
        this.updateRequests(resource.action, resource.data);
        break;
      case 'request_detail':
        this.updateRequestContents(resource.action, resource.data);
        break;
      case 'request_comment':
        this.updateRequestComments(resource.action, resource.data);
        break;
      case 'request_job':
        this.updateRequestJobs(resource.action, resource.data);
        break;
      case 'request_job_assignee':
        this.updateRequestJobAssignee(resource.action, resource.data);
        break;
      case 'request_job_result':
        this.updateRequestJobResults(resource.action, resource.data);
        break;
      }
    } catch (error) {
      throw error;
    }
  }

  private updateServiceCatalogs(action, data) {
    try {
      this.ServiceCatalog = this.ServiceCatalog || [];
      switch (action) {
      case 'insert':
        let sc:ServiceCatalog = new ServiceCatalog();
        sc.PropertiesFromStorage = data;
        this.ServiceCatalog.push(sc);
        sortObjectArray(this.ServiceCatalog,'Upline.Code,Name');
        break;
      default:
        let i = this.getServiceCatalogIndex(data.code);
        if (i>=0) {
          if (action=='update') {
            this.ServiceCatalog[i].PropertiesFromStorage = data;
            sortObjectArray(this.ServiceCatalog,'Upline.Code,Name');
          } else if (action=='delete') {
            try {
              this.ServiceCatalog.splice(i);
            } catch (e) {}
          }
        }
      }
    } catch (error) {
      throw error;
    }
  }

  private getServiceCatalogIndex(code:string) : number {
    let index:number = -1;
    let i;
    if (this.ServiceCatalog && this.ServiceCatalog.length>0) {
      for (i=0; i<this.ServiceCatalog.length; i++) {
        if (this.ServiceCatalog[i].Code = code) {
          index = i;
          break;
        }
      }
    }
    return index;
  }

  getRequestIndex(requestId:string) : number {
    let index:number = -1;
    if (this.Requests && this.Requests.length>0) {
      for (let i=0; i<this.Requests.length; i++) {
        if (this.Requests[i].HalloId == requestId) {
          index = i;
          break;
        }
      }
    }
    return index;
  }

  private updateRequests(action, data) {
    try {
      this.Requests = this.Requests || [];
      let i = this.getRequestIndex(data.id);
      if (i>=0) {
        if (action=='delete') {
          try {
            this.Requests.splice(i,1);
          } catch (e) {}
        } else {
          //some how data was pushed multiple time
          //whatever it is newly insert or update an existing one
          //we check if it exists in cache, we update it
          this.Requests[i].PropertiesFromStorage = data;
        }
      } else if (action=='insert') {
        let r:Request = new Request();
        r.PropertiesFromStorage = data;
        if (!r.Owner) {
          r.Owner = new User(this.Properties);
        }
        this.Requests.unshift(r);
      }
      try {
        sortObjectArray(this.Requests,'Dates.Updated desc,Status');
      } catch (e) {}
    } catch (error) {
      throw error;
    }
  }

  private updateRequestContents(action, data) {
    try {
      if (!this.Requests || this.Requests.length==0) {
        this.fetchRequests();
        return;
      }
      let i = this.getRequestIndex(data.request_id);
      if (i < 0) {
        return;
      }
      let request:Request = this.Requests[i];
      request.Contents = request.Contents || [];
      i = request.getContentIndex(data.id);
      if (i>=0) {
        if (action=='delete') {
          try {
            request.Contents.splice(i,1);
          } catch (e) {}
        } else {
          request.Contents[i].PropertiesFromStorage = data;
        }
      } else if (action=='insert') {
        let p:Paragraph = new Paragraph();
        p.PropertiesFromStorage = data;
        p.Request = request;
        request.Contents.unshift(p);
      }
    } catch (error) {
      throw error;
    }
  }

  private updateRequestComments(action, data) {
    try {
      if (!this.Requests || this.Requests.length==0) {
        this.fetchRequests();
        return;
      }
      let i = this.getRequestIndex(data.request_id);
      if (i < 0) {
        return;
      }
      let request:Request = this.Requests[i];
      request.Comments = request.Comments || [];
      i = request.getCommentIndex(data.id);
      if (i>=0) {
        if (action=='delete') {
          try {
            request.Comments.splice(i,1);
          } catch (e) {}
        } else {
          request.Comments[i].PropertiesFromStorage = data;
        }
      } else if (action=='insert') {
        let c:Comment = new Comment();
        c.PropertiesFromStorage = data;
        c.Request = request;
        request.Comments.unshift(c);
      }
    } catch (error) {
      throw error;
    }
  }
  
  private updateRequestJobs(action, data) {
    try {
      if (!this.Requests || this.Requests.length==0) {
        this.fetchRequests();
        return;
      }
      let i = this.getRequestIndex(data.request_id);
      if (i < 0) {
        return;
      }
      let request:Request = this.Requests[i];
      request.Jobs = request.Jobs || [];
      i = request.getJobIndex(data.id);
      if (i>=0) {
        if (action=='delete') {
          try {
            request.Jobs.splice(i,1);
          } catch (e) {}
        } else {
          request.Jobs[i].PropertiesFromStorage = data;
        }
      } else if (action=='insert') {
        let j:Job = new Job();
        j.PropertiesFromStorage = data;
        j.Request = request;
        request.Jobs.unshift(j);
      }
    } catch (error) {
      throw error;
    }
  }

  private async updateRequestJobAssignee(action, data) {
    try {
      if (!this.Requests || this.Requests.length==0) {
        this.fetchRequests();
        return;
      }
      let i = this.getRequestIndex(data.request_id);
      if (i < 0) {
        return;
      }
      let request:Request = this.Requests[i];
      i = request.getJobIndex(data.job_id);
      if (i < 0) {
        return;
      }
      let job = request.Jobs[i];
      i = job.getAssigneeIndex(data.id);
      job.Assignee = job.Assignee || [];
      if (i>=0) {
        if (action=='delete') {
          try {
            job.Assignee.splice(i,1);
          } catch (e) {}
        } else {
          job.Assignee[i].PropertiesFromStorage = data;
        }
      } else if (action=='insert') {
        let a:User = new User();
        a.PropertiesFromStorage = data;
        job.Assignee.unshift(a);
      }
    } catch (error) {
      throw error;
    }
  }
  
  private updateRequestJobResults(action, data) {
    try {
      if (!this.Requests || this.Requests.length==0) {
        this.fetchRequests();
        return;
      }
      let i = this.getRequestIndex(data.request_id);
      if (i < 0) {
        return;
      }
      let request:Request = this.Requests[i];
      i = request.getJobIndex(data.job_id);
      if (i < 0) {
        return;
      }
      let job = request.Jobs[i];
      i = job.getResultIndex(data.id);
      job.Results = job.Results || [];
      if (i>=0) {
        if (action=='delete') {
          try {
            job.Results.splice(i,1);
          } catch (e) {}
        } else {
          job.Results[i].PropertiesFromStorage = data;
        }
      } else if (action=='insert') {
        let p:Paragraph = new Paragraph();
        p.PropertiesFromStorage = data;
        p.Request = request;
        p.Job = job;
        job.Results.unshift(p);
      }
    } catch (error) {
      throw error;
    }
  }

  _ws;
  get WebSocket() {
    return this._ws;
  }
  openWebSocket(url?:string) {
    /*
    if (!this.Token && this.Token.hallo) {
      throw new Error("Login is required");
    }
    url = url || http.HALLO_WEBSOCKET + '/' + this.HalloId + '/' + this.Token.hallo;
    this._ws = new WS(url);
    this._ws.on("open", (websocket) => {
      this.onWebSocketOpen(websocket);
    });
    this._ws.on("close", (websocket, code, reason) => {
      this.onWebSocketClose(websocket, code, reason);
    });
    this._ws.on("error", (websocket, error) => {
      this.onWebSocketError(websocket, error);
    });
    this._ws.on("message", (websocket, message) => {
      this.onWebSocketMessage(websocket, message);
    });
    this._ws.open();*/
  }

  reconnectOnClose: boolean = true;
  closeWebSocket() {
    if (this._ws) {
      this.reconnectOnClose = false;
      this._ws.close();
    }
  }

  sendMessage(message:string) {
    if (this._ws) {
      this._ws.send(message);
    }
  }

  private onWebSocketOpen(websocket) {

  }

  private onWebSocketClose(websocket, code, reason) {
    if (this.reconnectOnClose) {
      this._ws._url = http.HALLO_WEBSOCKET + '/' + this.HalloId + '/' + this.Token.hallo;
      this._ws.open();
    }
    this.reconnectOnClose = true;
  }

  private onWebSocketError(websocket, error) {
    console.log("WebSocket Error: " + error);
  }

  private onWebSocketMessage(websocket, message) {
    if (!message) return;
    let m = JSON.parse(message);
    switch (m.type) {
      case 'notification_data':
        switch (m.payload.action) {
          case 'insert':
            //global.myGlobals.sounds.impressed.play();
            break;
          case 'update':
            //global.myGlobals.sounds.springBoard.play();
            break;
          case 'delete':
          default:
            //global.myGlobals.sounds.plucky.play();
            break;
        }
        this.updateResource(m.payload);
        break;
      case 'chat':
        //global.myGlobals.sounds.springBoard.play();
        break;
      case 'reconnection': 
        //every time when open a ws connection, the ws server will
        //issue a token back. user can use it to reconnect to the ws server
        this.Token.hallo = m.token;
    }
    if (m.type=='notification_data' && m.payload && m.payload.data) {
      this.updateResource(m.payload);
      switch (m.payload.action) {
        case 'insert':
          //global.myGlobals.sounds.impressed.play();
          break;
        case 'update':
          //global.myGlobals.sounds.springBoard.play();
          break;
        case 'delete':
        default:
          //global.myGlobals.sounds.plucky.play();
          break;
      }
    } else if (m.type=='chat') {
      //global.myGlobals.sounds.springBoard.play();
    }
  }
}